import axios from "axios";

const appBaseUrl = process.env.REACT_APP_API_ADMIN_BASE_URL;

// Helper function to get authentication headers
const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  
  if (token) {
    return {
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json"
    };
  }
  
  throw new Error("No authentication token found");
};

const internalSettlementApi = axios.create({
  baseURL: appBaseUrl,
  withCredentials: true,
});


export const processInternalSettlement = async (partnerId, amount, description = 'Internal admin settlement') => {
  try {
    
    const response = await internalSettlementApi.post('/admin/internal-settlement', {
      partner_id: partnerId,
      amount: amount,
      description: description
    }, {
      headers: getAuthHeaders()
    });

    return response.data;
  } catch (error) {
    
    const errorMessage = error.response?.data?.message || 
                        error.message || 
                        'Failed to process internal settlement';
    
    throw new Error(errorMessage);
  }
};

export const getSettlementHistory = async (partnerId = null, page = 1, perPage = 50, startDate = null, endDate = null, status = null) => {
  try {
    
    const params = new URLSearchParams({
      per_page: perPage.toString()
    });
    
    if (partnerId) {
      params.append('partner_id', partnerId);
    }
    if (startDate) {
      params.append('start_date', startDate);
    }
    if (endDate) {
      params.append('end_date', endDate);
    }
    if (status) {
      params.append('status', status);
    }
    
    const response = await internalSettlementApi.get(`/admin/offline-settlements?${params}`, {
      headers: getAuthHeaders()
    });
    
    return response.data;
  } catch (error) {
    // console.error("❌ Settlement history error:", error);
    
    const errorMessage = error.response?.data?.message || 
                        error.message || 
                        'Failed to fetch settlement history';
    
    throw new Error(errorMessage);
  }
};

export const getPartnerList = async () => {
  try {
    
    // Try to get partners from analytics data first
    const response = await internalSettlementApi.get('/analytics/partners', {
      headers: getAuthHeaders()
    });
    
    return response.data;
  } catch (error) {
    // console.error("❌ Partner list error:", error);
    
    // Fallback: return empty list if partners endpoint doesn't exist
    return {
      success: true,
      data: [],
      message: "No partners endpoint available"
    };
  }
};

export const getSettlementsByPartner = async (partnerId, startDate = null, endDate = null, status = null, perPage = 50) => {
  try {
    
    const params = new URLSearchParams({
      per_page: perPage.toString()
    });
    
    if (startDate) {
      params.append('start_date', startDate);
    }
    if (endDate) {
      params.append('end_date', endDate);
    }
    if (status) {
      params.append('status', status);
    }
    
    const response = await internalSettlementApi.get(`/admin/offline-settlements/partner/${partnerId}?${params}`, {
      headers: getAuthHeaders()
    });
    
    return response.data;
  } catch (error) {
    // console.error("❌ Settlements by partner error:", error);
    
    const errorMessage = error.response?.data?.message || 
                        error.message || 
                        'Failed to fetch settlements by partner';
    
    throw new Error(errorMessage);
  }
};

export const getSettlementsByDate = async (startDate, endDate, partnerId = null, status = null, perPage = 50) => {
  try {
    
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      per_page: perPage.toString()
    });
    
    if (partnerId) {
      params.append('partner_id', partnerId);
    }
    if (status) {
      params.append('status', status);
    }
    
    const response = await internalSettlementApi.get(`/admin/offline-settlements/by-date?${params}`, {
      headers: getAuthHeaders()
    });
    
    return response.data;
  } catch (error) {
    // console.error("❌ Settlements by date error:", error);
    
    const errorMessage = error.response?.data?.message || 
                        error.message || 
                        'Failed to fetch settlements by date';
    
    throw new Error(errorMessage);
  }
};

export const getSettlementStats = async (startDate = null, endDate = null) => {
  try {
    
    const params = new URLSearchParams();
    
    if (startDate) {
      params.append('start_date', startDate);
    }
    if (endDate) {
      params.append('end_date', endDate);
    }
    
    const response = await internalSettlementApi.get(`/admin/offline-settlements/stats?${params}`, {
      headers: getAuthHeaders()
    });
    
    return response.data;
  } catch (error) {
    // console.error("❌ Settlement stats error:", error);
    
    const errorMessage = error.response?.data?.message || 
                        error.message || 
                        'Failed to fetch settlement statistics';
    
    throw new Error(errorMessage);
  }
};

export const getPartnerBalance = async (partnerId) => {
  try {
    
    // Use the existing partner endpoint to get partner data including balance
    const response = await internalSettlementApi.get(`/partner/${partnerId}`, {
      headers: getAuthHeaders()
    });
    
    
    // Extract balance information from partner data
    if (response.data && response.data.data) {
      const partnerData = response.data.data;
      return {
        success: true,
        data: {
          payin_balance: partnerData.payin_balance || 0,
          payout_balance: partnerData.payout_balance || 0,
          partner_id: partnerData.pid,
          business_name: partnerData.business_name || partnerData.fullname
        }
      };
    }
    
    return response.data;
  } catch (error) {
    // console.error("❌ Partner balance error:", error);
    
    const errorMessage = error.response?.data?.message || 
                        error.message || 
                        'Failed to fetch partner balance';
    
    throw new Error(errorMessage);
  }
};
